import React from 'react'
import { useTranslation } from 'react-i18next'
import { Award, Briefcase } from 'lucide-react'
import PropTypes from 'prop-types'

const TeamMemberCard = ({ member }) => {
  const { t } = useTranslation()
  // Inline SVG dummy avatar (no external file dependency)
  const DUMMY_AVATAR =
    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="256" height="256" viewBox="0 0 256 256"><rect width="100%" height="100%" fill="%23f3f4f6"/><circle cx="128" cy="92" r="44" fill="%23d1d5db"/><path d="M40 220c0-44 36-80 88-80s88 36 88 80" fill="%23d1d5db"/></svg>'

  const imageUrl = (member?.image?.url || '').trim()
  const src = imageUrl ? imageUrl : DUMMY_AVATAR

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden max-w-sm mx-auto">
      {/* Image Container */}
      <div className="w-full h-64 bg-gray-100 flex items-center justify-center overflow-hidden">
        <img
          src={src}
          alt={member.name}
          className="max-h-full max-w-full object-contain"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.target.onerror = null // prevent infinite loop if fallback fails
            e.target.src = DUMMY_AVATAR
          }}
        />
      </div>

      {/* Content */}
      <div className="p-6 text-center">
        <h3 className="font-bold text-gray-900 text-xl mb-3">
          {member.name}
        </h3>
        
        <div className="inline-block bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4 uppercase tracking-wide">
          {member.position}
        </div>

        {member.qualifications && member.qualifications.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Award className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{t('common.qualification')}</span>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed">
              {member.qualifications.filter(q => q && q.trim()).join(', ')}
            </p>
          </div>
        )}

        {member.experience && (
          <div className="flex items-center justify-center gap-2 text-purple-600 text-sm font-medium">
            <Briefcase className="w-4 h-4" />
            <span>{t('common.experience')}: {member.experience}</span>
          </div>
        )}
      </div>
    </div>
  )
}

TeamMemberCard.propTypes = {
  member: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string.isRequired,
    position: PropTypes.string.isRequired,
    image: PropTypes.shape({
      url: PropTypes.string,
      publicId: PropTypes.string,
    }),
    bio: PropTypes.string,
    qualifications: PropTypes.arrayOf(PropTypes.string),
    experience: PropTypes.string,
  }).isRequired,
}

export default TeamMemberCard
